/**
 * Asking the hub about a device credential WITHOUT spending it (card
 * `t_7aa9066f`).
 *
 * The credential's own route is `/auth/device/refresh`, and `POST` on it is a
 * **renewal**: the hub mints the binding's successor, stores its hash, and the
 * credential that was presented is refused from that answer on (one slot in
 * `device_bindings.token_hash`, no grace window — measured on `Rožna S1`, card
 * `t_6b11b40d`). That is exactly right for a client that is about to use its
 * credential again, and exactly wrong for a client that only wants to ASK: a
 * probe of the POST that lost its answer left a field tablet holding a
 * credential its hub now refuses, with no keyboard and no admin to re-pair it.
 *
 * So a probe asks `GET` on the same path (hub `9b8e509`), which verifies through
 * the same verifier and writes nothing at all, and answers in the refresh
 * route's own vocabulary:
 *
 *     GET /api/auth/device/refresh → 200 {valid:true, kind:"binding", …}
 *                                  → 401 {code:"no_token" | "invalid_device_token"}
 *
 * Three readings decide this module, and every one of them is cheap to get
 * wrong:
 *
 *   1. a `200` is a verdict WITHOUT a spend — the credential is good, nothing is
 *      rotated, and the 401 that raised the question was about something else (a
 *      route that wants a user JWT, card `t_68d110ee`'s class);
 *   2. `code:"invalid_device_token"` is the verdict, on either verb: the
 *      credential is dead and the device has to enroll again;
 *   3. **a 404 (or 405) is not a verdict.** It says this hub has no probe route
 *      — every hub in the field until a release carrying the route lands — and
 *      the question then falls back to the renewal, exactly as this app asked
 *      before the probe existed. Nothing about the credential is learnt from a
 *      404, so the credential must not be dropped for one.
 *
 * A network failure or a 5xx is neither: the credential stays and no verdict is
 * returned. The sequence below is a function of the steps it is handed (the GET,
 * the renewal, what a refusal does to the session), so the policy can be tested
 * without a hub, a browser or a pinia — `deviceCredentialProbe.test.ts` does.
 */
import { isDeviceCredentialRejected } from './deviceCredential';

/** The credential's own route, where both the probe and the renewal live. */
export const DEVICE_CREDENTIAL_PROBE_ENDPOINT = '/auth/device/refresh';

/** Statuses that mean "this hub does not serve the probe route" (`GET`). */
const PROBE_ROUTE_MISSING_STATUSES: readonly number[] = [404, 405];

/**
 * What the renewal (`POST /auth/device/refresh`) says about the credential it
 * was presented with. Defined here because the probe has to read it: it is the
 * one answer a hub without a probe route can give.
 */
export type DeviceRenewalOutcome = 'rotated' | 'invalid' | 'unavailable' | 'none';

/**
 * The verdict on the credential, after the whole question has been asked.
 *  - `valid`       the hub verifies it (the probe's `200`, or a renewal);
 *  - `invalid`     the hub refuses it — it is dropped and the device re-enrolls;
 *  - `unavailable` no verdict (network failure, 5xx, an answer that is no
 *                  verdict about the credential);
 *  - `none`        there was nothing to ask about.
 */
export type DeviceProbeVerdict = 'valid' | 'invalid' | 'unavailable' | 'none';

/** What ONE probe attempt's failure reads as. */
export type ProbeFailureReading = 'invalid' | 'no_route' | 'unavailable';

/**
 * Is this "the hub has no probe route"?
 *
 * 404 is an older hub — the route is simply not mounted, whole fleet today. 405
 * is a hub that knows the path but not this verb. Neither is an answer about the
 * credential, and reading one as a refusal is how a perfectly good credential
 * gets thrown away.
 */
export function isDeviceProbeRouteMissing(error: unknown): boolean {
  const status = (error as { status?: unknown } | null | undefined)?.status;
  return typeof status === 'number' && PROBE_ROUTE_MISSING_STATUSES.includes(status);
}

/**
 * Read a failed probe attempt.
 *
 * The credential's own code is the only unambiguous refusal
 * (`isDeviceCredentialRejected` owns that rule); a 404/405 is "no probe route";
 * everything else — a 401 from a route that wants a user JWT, a 500, a fetch
 * that never arrived — is no verdict at all.
 */
export function classifyProbeFailure(error: unknown): ProbeFailureReading {
  if (isDeviceCredentialRejected(error)) return 'invalid';
  if (isDeviceProbeRouteMissing(error)) return 'no_route';
  return 'unavailable';
}

/** The verdict a renewal's outcome amounts to. */
export function renewalVerdict(outcome: DeviceRenewalOutcome): DeviceProbeVerdict {
  switch (outcome) {
    case 'rotated':
      // The hub accepted the credential and handed back its successor.
      return 'valid';
    case 'invalid':
      return 'invalid';
    case 'none':
      return 'none';
    default:
      return 'unavailable';
  }
}

export interface DeviceCredentialProbeStep {
  /** The credential in hand, or null. Nothing is asked without one. */
  credential: string | null;
  /**
   * `GET /auth/device/refresh` with the credential — the probe. Resolves when
   * the hub verified it; throws the API error when it did not.
   */
  probe: (token: string) => Promise<unknown>;
  /**
   * The renewal — today's path, the `POST`. Reached ONLY where the probe route
   * is missing, so a hub without the route is asked exactly what it was asked
   * before the probe existed. A refusal that arrives this way is the renewal's
   * own business (it drops and announces the credential itself), which is why
   * `refuse` is not called for it.
   */
  renew: () => Promise<DeviceRenewalOutcome>;
  /**
   * The hub refused the credential (the probe's own `invalid_device_token`):
   * forget it and tell the person. Called once per refusal — a refused
   * credential is never asked about again, which is what keeps this from
   * looping.
   */
  refuse: () => void | Promise<void>;
}

/**
 * The whole question, asked in the one order that cannot spend a credential:
 * probe first, renewal only where the hub has no probe route.
 */
export async function runDeviceCredentialProbe(step: DeviceCredentialProbeStep): Promise<DeviceProbeVerdict> {
  const token = step.credential;
  if (!token) return 'none';

  try {
    //`GET` — verify, write nothing. A 200 means the credential is good; it is
    // left in place, with no successor to adopt and nothing to drop.
    await step.probe(token);
    return 'valid';
  } catch (error) {
    const reading = classifyProbeFailure(error);
    if (reading === 'invalid') {
      await step.refuse();
      return 'invalid';
    }
    if (reading === 'no_route') {
      // This hub has no probe route: ask the way this app always has. The
      // renewal rotates on use — which is correct here, because it is a
      // renewal: the successor it returns replaces the credential presented.
      return renewalVerdict(await step.renew());
    }
    // No verdict: a network blip, a 5xx, or an answer that is not the probe
    // route's vocabulary. The credential stays — it was not spent, and it has
    // not been refused.
    return 'unavailable';
  }
}
